// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// interfaces/types
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import type { ForSharing } from '../types/types';

import {
    PRINT_CUSTOMIZABLE_PDF,
    PRINT_PDF_FOR_TEXT,
    PRINT_PDF_FOR_EMAIL,
    PUBLISH_TO_SHOWROOM,
    GENERATE_PDF
} from '../actionTypes/printPdfActionTypes';
import { OFFER_SAVE_SUCCESS, OFFER_SAVE_FAILURE } from '../actionTypes/offerActionTypes';

// selectors
import * as offerReduxSelectors from '../selectors/offerRedux';

// actions
import { saveOffer } from '../actions/offerActionCreators';
import { printPdfLoading } from '../actions/printPdfActionCreator';

// services
import { callPdfService } from '../services/printPdfService';
import { callGeneratePdfService } from '../services/generatePdf';

type PrintFlow = { forSharing: ForSharing; suppressOutput: boolean };

const PRINT_FLOWS: Record<string, PrintFlow> = {
    [PRINT_CUSTOMIZABLE_PDF]: { forSharing: false, suppressOutput: false },
    [PRINT_PDF_FOR_TEXT]: { forSharing: 'text', suppressOutput: false },
    [PRINT_PDF_FOR_EMAIL]: { forSharing: 'email', suppressOutput: false },
    [PUBLISH_TO_SHOWROOM]: { forSharing: false, suppressOutput: true }
};

// The middleware already saved via saveOffer, so callPdfService must not double-save.
const generatePdfFor = (store: DashStore, action: AnyFSA, flow: PrintFlow, saveFailed = false) => {
    const { selectedProposalId, selectedScopeIds, scopeGridSelections } = action.payload ?? {};
    return callPdfService(store, flow.forSharing, selectedProposalId, selectedScopeIds, {
        suppressOutput: flow.suppressOutput,
        scopeGridSelections,
        saveFailed,
        skipSave: true
    });
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => async (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case PRINT_CUSTOMIZABLE_PDF:
        case PRINT_PDF_FOR_TEXT:
        case PRINT_PDF_FOR_EMAIL:
        case PUBLISH_TO_SHOWROOM: {
            const flow = PRINT_FLOWS[action.type];
            store.dispatch(printPdfLoading(true, flow.forSharing, flow.suppressOutput));

            if (offerReduxSelectors.getIsDealXgRecordMissed(store.getState())) {
                logNewRelicPageAction('MV:PrintSaveBeforePdfSkipped', { flow: action.type, reason: 'no-dealxg-record' });
                await generatePdfFor(store, action, flow);
                break;
            }

            store.dispatch(saveOffer({ isDraft: false, originalAction: action }));
            break;
        }
        case OFFER_SAVE_SUCCESS: {
            const originalAction = action.meta?.originalAction;
            const flow = originalAction?.type ? PRINT_FLOWS[originalAction.type] : undefined;
            if (!flow) break;
            await generatePdfFor(store, originalAction, flow);
            break;
        }
        case OFFER_SAVE_FAILURE: {
            const originalAction = action.meta?.originalAction;
            const flow = originalAction?.type ? PRINT_FLOWS[originalAction.type] : undefined;
            if (!flow) break;
            logNewRelicPageAction('MV:PrintSaveBeforePdfFailed', { flow: originalAction.type });
            await generatePdfFor(store, originalAction, flow, true);
            break;
        }
        case GENERATE_PDF: {
            const { type } = action.payload || {};
            await callGeneratePdfService(store, type);
            break;
        }
    }
};
